import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useOrganization } from "../../../hooks/queries";
import { LocationData } from "../../../models/LocationData";
import DataSvc from "../../../services/dataSvc";
import {
  buildCsvFilename,
  insertOrgAndFacilityIds,
} from "../../../utils/csvUtils";
import { TimeUnitSelect } from "../../AnalysisCollectionsComponents/CollectionChartArea/LineChart/TimeUnitSelect";
import { TextFileDownload } from "../../Common/TextFileDownload";
import { TimespanPicker } from "../TimespanPicker";
import { useTimespanPicker } from "../TimespanPicker/hooks";
import "./styles.scss";

type IDownloadMetricsProps = {
  locations: LocationData[];
  organizationId: string;
};

export const DownloadMetrics: React.FC<IDownloadMetricsProps> = ({
  locations,
  organizationId,
}) => {
  const { t: _t } = useTranslation();
  const t = (key: string) => _t(`dataDownloadPage.downloadMetrics.${key}`);

  // Query organization data based on the organizationId from props
  const { data: organization } = useOrganization(organizationId);

  const [timeUnit, setTimeUnit] = useState<"day" | "week" | "month">("day");
  const timespan = useTimespanPicker();

  const queryKey = useMemo(
    () => [
      "download-metrics",
      timespan.timespan,
      timeUnit,
      locations.map((location) => location.id.toString()),
    ],
    [timespan, timeUnit, locations]
  );
  const queryFunction = useCallback(async () => {
    const data = await DataSvc.getFacilityDataMetrics({
      organizationId,
      timespan: timespan.timespan,
      timeUnit,
      locations,
    });

    // Create facility mapping for ID insertion
    const facilityMapping = new Map<string, string | number>();
    locations.forEach((loc) => {
      facilityMapping.set(loc.name, loc.id);
    });

    // Create organization mapping for ID insertion
    // orgMapping key can be any placeholder since we only use the ID
    const orgMapping = new Map<string, string | number>([
      ["org", organizationId],
    ]);

    // Insert organization and facility IDs into the CSV
    const processedData = insertOrgAndFacilityIds(
      data,
      orgMapping,
      facilityMapping
    );

    // Build standardized filename
    const filename = buildCsvFilename(
      {
        startDate: timespan.timespan.startDate?.format("YYYY-MM-DD"),
        endDate: timespan.timespan.endDate?.format("YYYY-MM-DD"),
        organizationName: organization?.name,
        organizationId,
        contentDescription: "metrics",
      },
      _t
    );

    return { filename, content: processedData };
  }, [timespan, timeUnit, locations, organizationId, _t, organization]);

  return (
    <div className="white-border-panel download-metrics">
      <h3>{t("download_metrics")}</h3>
      <div className="content">
        <div className="download-metrics-controls">
          <TimespanPicker timespan={timespan} />
          <TimeUnitSelect timeUnit={timeUnit} setTimeUnit={setTimeUnit} />
        </div>
        <TextFileDownload queryKey={queryKey} queryFunction={queryFunction} />
      </div>
    </div>
  );
};
